import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { VipUpgradesService } from '../../services/sales/box-office/vip-upgrades.service';
import * as VipUpgradesActions from './vip-upgrades.actions';

@Injectable()
export class VipUpgradesEffects {
    private _actions$ = inject(Actions);
    private _vipUpgradesService = inject(VipUpgradesService);
    private adapter = inject(AdapterService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all VIP upgrades',
        getOneError: 'Error getting a VIP upgrade',
        updateError: 'Error updating the VIP upgrade',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All VipUpgrades
    getAllVipUpgrades$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VipUpgradesActions.loadVipUpgrades),
            switchMap((action) => {
                return this._vipUpgradesService.getAll(action.playdateId).pipe(
                    map((res) => {
                        const data = res.data;

                        if (!data) {
                            throw new Error(this.messages.getAllError);
                        }

                        const sales = this.adapter.adaptVipUpgradesList(data);

                        return VipUpgradesActions.loadVipUpgradesSuccess({
                            vipUpgrades: sales,
                        });
                    }),
                    catchError((error) =>
                        of(
                            VipUpgradesActions.loadVipUpgradesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One VipUpgrade
    // getVipUpgrade$ = createEffect(() =>
    //     this._actions$.pipe(
    //         ofType(VipUpgradesActions.loadVipUpgrade),
    //         switchMap((action) => {
    //             return this._vipUpgradesService.getOne(action.id).pipe(
    //                 map((res) => {
    //                     return VipUpgradesActions.loadVipUpgradeSuccess({
    //                         vipUpgrade: res.data,
    //                     });
    //                 }),
    //                 catchError((error) =>
    //                     of(
    //                         VipUpgradesActions.loadVipUpgradeFailure({
    //                             error:
    //                                 error?.error?.message ||
    //                                 this.messages.unknownError,
    //                         })
    //                     )
    //                 )
    //             );
    //         })
    //     )
    // );

    // Create VipUpgrade
    createVipUpgrade$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VipUpgradesActions.createVipUpgrade),
            switchMap((action) => {
                return this._vipUpgradesService.create(action.data).pipe(
                    map((res) => {
                        const data = res.data;
                        if (!data) {
                            throw new Error(this.messages.getOneError);
                        }

                        const sale = this.adapter.adaptVipUpgrade(data);

                        if (!sale) {
                            throw new Error(this.messages.getOneError);
                        }

                        return VipUpgradesActions.createVipUpgradeSuccess({
                            vipUpgrade: sale,
                            success: 'VIP upgrade created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            VipUpgradesActions.createVipUpgradeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update VipUpgrade
    updateVipUpgrade$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VipUpgradesActions.updateVipUpgrade),
            switchMap((action) => {
                return this._vipUpgradesService.update(action.data).pipe(
                    map((res) => {
                        const data = res.data;
                        if (!data) {
                            throw new Error(this.messages.updateError);
                        }

                        const sale = this.adapter.adaptVipUpgrade(data);

                        if (!sale) {
                            throw new Error(this.messages.getOneError);
                        }

                        return VipUpgradesActions.updateVipUpgradeSuccess({
                            vipUpgrade: sale,
                            success: 'VIP upgrade updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            VipUpgradesActions.updateVipUpgradeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Vip Upgrade
    deleteVipUpgrade$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VipUpgradesActions.deleteVipUpgrade),
            switchMap((action) => {
                return this._vipUpgradesService.delete(action.payload).pipe(
                    map((res) => {
                        const deletedSales = res.data.deleted_sales;
                        return VipUpgradesActions.deleteVipUpgradeSuccess({
                            deletedSales: deletedSales,
                            success: 'VIP upgrade deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            VipUpgradesActions.deleteVipUpgradeFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Vip Upgrade Item
    deleteVipUpgradeItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VipUpgradesActions.deleteVipUpgradeItem),
            switchMap((action) => {
                return this._vipUpgradesService.delete(action.payload).pipe(
                    map((res) => {
                        const saleId = res.data.deleted_sales[0];

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return VipUpgradesActions.deleteVipUpgradeItemSuccess({
                            deletedSaleItem: saleId,
                            success: 'VIP upgrade item deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            VipUpgradesActions.deleteVipUpgradeItemFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onVipUpgradeActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    VipUpgradesActions.createVipUpgradeSuccess,
                    VipUpgradesActions.updateVipUpgradeSuccess,
                    VipUpgradesActions.deleteVipUpgradeSuccess,
                    VipUpgradesActions.deleteVipUpgradeItemSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                })
            ),
        { dispatch: false }
    );

    onVipUpgradeActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    VipUpgradesActions.loadVipUpgradesFailure,
                    VipUpgradesActions.loadVipUpgradeFailure,
                    VipUpgradesActions.createVipUpgradeFailure,
                    VipUpgradesActions.updateVipUpgradeFailure,
                    VipUpgradesActions.deleteVipUpgradeFailure,
                    VipUpgradesActions.deleteVipUpgradeItemFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
